import { Box } from "@mui/material";
import { memo, useMemo } from "react";
import { Carousel } from "../discover/carousel/carousel";
import { TemplateCarouselImage } from "./template-carousel-image";
import { useTemplateImageDimensions } from "./template-image";

import type { CarouselItemProps } from "../discover/carousel/carousel";
import type { LoadableTemplateImage } from "../generic/atoms/types/template";
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import type { BoxProps } from "@mui/material";
import type { FC } from "react";

export const TemplateImagesCarousel: FC<
  Omit<BoxProps, "onSelect"> & {
    images: LoadableTemplateImage[];
    selectedImage?: number;
    onSelect?: (n: number) => void;
  }
> = ({ images, selectedImage, onSelect, ...props }) => {
  const dimensions = useTemplateImageDimensions();
  const ratio = dimensions.width / dimensions.height;
  const height = 111;
  const width = height * ratio;

  const templateImages = useMemo(
    () =>
      images.length === 0
        ? Array.from(
            { length: 9 },
            () =>
              ({
                state: "loading",
              }) as LoadableTemplateImage,
          )
        : images,
    [images],
  );

  return (
    <Box {...props}>
      <Carousel
        items={templateImages.map((image, i) => ({
          ...image,
          key: i.toString(),
        }))}
        Item={Item}
        ItemExtraProps={{
          onSelect,
          selected: (i: number) => selectedImage === i,
          opacity: (i: number) => (selectedImage === i ? 1 : 0.8),
          width: `${width}px`,
          height: `${height}px`,
        }}
        itemWidth={`${width}px`}
        gap={2}
        sx={(theme) => ({
          "--carousel-bgColor": theme.palette.grey[100],
        })}
      />
    </Box>
  );
};

interface ExtraProps {
  onSelect?: (n: number) => void;
  selected: (i: number) => boolean;
  opacity: (i: number) => number;
  width: string;
  height: string;
}

const Item = memo(
  ({
    value: image,
    i,
    extraProps: { onSelect, selected, opacity, width, height },
    ...props
  }: CarouselItemProps<LoadableTemplateImage, ExtraProps>) => (
    <TemplateCarouselImage
      key={`img-${i}`}
      image={image}
      selected={selected(i)}
      onClick={() => onSelect?.(i)}
      sx={{
        opacity: opacity(i),
        width,
        height,
        ":hover": {
          opacity: 1,
        },
      }}
      {...props}
    />
  ),
);
